import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewExport from '@app/src/components/Drawer/Views/DrawerViewExport'
import { usePageBreadcrumbs } from '@app/src/components/Navigation/Breadcrumbs/usePageBreadcrumbs'
import Table from '@app/src/components/Table'
import { MappingNodeExportColumns } from '@app/src/export-columns/mappingNode'
import usePaginationWithQuery from '@app/src/hooks/paginationWithQuery'
import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import useSort from '@app/src/hooks/sorting'
import MappingNodeHeader from '@app/src/pages/ResourceCollection/Collections/MappingNode/MappingNodeHeader'
import MappingNodeRow from '@app/src/pages/ResourceCollection/Collections/MappingNode/MappingNodeRow'
import useProductTabs, {
  SUPPLY_CHAIN_RELATIONS_TAB,
} from '@app/src/pages/ResourceCollection/Collections/ProductsMrt/useProductTabs'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { MappingNode } from '@app/src/types/product'
import { ResourceTypes } from '@app/src/wf-constants'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React from 'react'
import { useIntl } from 'react-intl'
import MappingNodeFilters from '../Filters/MappingNodeFilters'
import ResourceCollectionScene from '../ResourceCollectionScene'

type MappingNodeCollectionProps = {
  relatedContentFilter?: FilterGroup[]
  embedded?: boolean
}

const allowedFilters: string[] = [
  'name',
  'tier',
  'product.id',
  'product.name',
  'product.gtin',
  'parentNode.id',
  'actorType.name',
  'provider.organization.id',
  'provider.spends.spendClassification',
  'provider.spends.periodName',
  'parentNode.provider.organization.id',
  'parentNode.actorType.name',
  'product.purchaseOrder',
  'product.productNumber',
  'product.productCustomId',
  'product.productStatus',
]

const MappingNodeCollection: React.FC<MappingNodeCollectionProps> = ({ relatedContentFilter = [], embedded }) => {
  const { formatMessage } = useIntl()
  const { newProductMappingUi } = useFlags()
  const { sorting, toggleSorting } = useSort()
  const { openDrawer } = useDrawer()
  const filters = useGetApiQueryFilters(allowedFilters)
  const [page, pageSize, setPage, setPageSize] = usePaginationWithQuery(undefined, [filters])
  const productTabs = useProductTabs()

  // In the new product mapping UI this collection is a tab on the Products page instead of a page of its own.
  const isProductsTab = Boolean(newProductMappingUi) && !embedded

  const productMappingCrumb = { label: formatMessage({ id: 'navbar.productMapping' }) }
  const productsCrumb = { label: formatMessage({ id: 'navbar.allProducts' }) }
  const valueChainsCrumb = { label: formatMessage({ id: 'navbar.valueChains' }) }
  const crumbs = isProductsTab
    ? [productMappingCrumb, productsCrumb, valueChainsCrumb]
    : [productMappingCrumb, valueChainsCrumb]
  usePageBreadcrumbs(embedded ? null : crumbs)

  const rawExportPayload = {
    filter: [
      {
        name: 'tier',
        filters: [{ value: '0', operator: Operators.NotEqualTo }],
      },
      ...relatedContentFilter,
    ],
    sort: sorting,
    include: [
      'product',
      'actorType',
      'provider.organization',
      'provider.organization.country',
      'provider.organization.industryNaceCode',
      'provider.organization.subIndustryNaceCode',
      'provider.providerOwners.user',
      'provider.organization.image',
      'provider.organization.locations',
      'parentNode.organization',
      'parentNode.organization.country',
      'parentNode.organization.industryNaceCode',
      'parentNode.organization.subIndustryNaceCode',
      'parentNode.organization.image',
      'parentNode.organization.locations',
      'parentNode.provider',
      'parentNode.actorType',
      'parentNode.provider.organization',
      'parentNode.provider.organization.country',
      'parentNode.provider.organization.industryNaceCode',
      'parentNode.provider.organization.subIndustryNaceCode',
      'parentNode.provider.organization.image',
      'parentNode.provider.organization.locations',
    ],
  }

  const payload = {
    ...rawExportPayload,
    filter: [...filters, ...rawExportPayload.filter],
    pagination: {
      pageNumber: page,
      itemsPerPage: pageSize,
    },
  }

  const {
    items: mappingNodes,
    count,
    isLoading,
    isFetching,
    isError,
  } = useFetchCollectionWithPost<MappingNode>({
    key: FetchKey.MappingNodeCollection,
    endpoint: endpoints.mappingNodeCollection,
    payload,
  })

  return (
    <>
      <ResourceCollectionScene
        title={formatMessage({ id: isProductsTab ? 'navbar.allProducts' : 'navbar.valueChains' })}
        tabs={isProductsTab ? { actionTabs: productTabs, activeTabParam: SUPPLY_CHAIN_RELATIONS_TAB } : undefined}
        showTabsBottomBorder
        actionButtons={[
          {
            label: formatMessage({ id: 'resourceCollections.general.export' }),
            variant: 'outlined',
            onClick: () =>
              openDrawer(
                <DrawerViewExport
                  resourceType={ResourceTypes.MappingNode}
                  count={count}
                  userFilter={filters}
                  exportColumns={MappingNodeExportColumns}
                  rawExportPayload={rawExportPayload}
                />,
              ),
            disabled: isLoading || count === 0,
          },
        ]}
        filter={<MappingNodeFilters allowedFilters={allowedFilters} implicitFilters={rawExportPayload.filter} />}
        embedded={embedded}
      >
        <Table<MappingNode>
          HeaderComponent={() => <MappingNodeHeader toggleSorting={toggleSorting} activeSorting={sorting} />}
          RowComponent={MappingNodeRow}
          data={mappingNodes}
          count={count}
          page={page}
          isError={isError}
          isLoading={isLoading || isFetching}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={setPageSize}
          stickyColumnIndex={1}
        />
      </ResourceCollectionScene>
    </>
  )
}

export default MappingNodeCollection
